import { useState, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { db, callEdgeFunction, isAbortError } from '../../lib/supabase'
import { formatDate } from '../../lib/dateUtils'
import { ErrorAlert } from '../../components/ui'
import { SALUTATIONS, PROFESSIONAL_ROLES } from '../../lib/constants'

const STATUS_STYLES = {
  pending_approval: 'bg-amber-100 text-amber-700',
  active: 'bg-green-100 text-green-700',
  suspended: 'bg-red-100 text-red-700',
  rejected: 'bg-gray-200 text-gray-700',
}

function getPairStatus(pair, t) {
  if (pair.male_survey_completed_at && pair.female_survey_completed_at) return { label: t('facilitatorDetail.pairStatuses.bothCompleted'), style: 'bg-green-100 text-green-700' }
  if (pair.male_survey_completed_at || pair.female_survey_completed_at) return { label: t('facilitatorDetail.pairStatuses.partiallyCompleted'), style: 'bg-blue-100 text-blue-700' }
  if (pair.male_password_set_at || pair.female_password_set_at) return { label: t('facilitatorDetail.pairStatuses.inProgress'), style: 'bg-indigo-100 text-indigo-700' }
  return { label: t('facilitatorDetail.pairStatuses.invited'), style: 'bg-gray-100 text-gray-600' }
}

const inputClass = "w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm transition-all focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100"

export default function FacilitatorDetail() {
  const { t, i18n } = useTranslation('admin')
  const salutationLabels = i18n.t('salutations', { ns: 'common', returnObjects: true })
  const roleLabels = i18n.t('professionalRoles', { ns: 'common', returnObjects: true })
  const locale = i18n.language?.startsWith('zh') ? 'zh-CN' : 'en'

  const translateSalutation = (val) => {
    if (!val) return val
    const idx = SALUTATIONS.indexOf(val)
    return idx >= 0 && Array.isArray(salutationLabels) ? salutationLabels[idx] || val : val
  }
  const translateRole = (val) => {
    if (!val) return val
    const idx = PROFESSIONAL_ROLES.indexOf(val)
    return idx >= 0 && Array.isArray(roleLabels) ? roleLabels[idx] || val : val
  }

  const { id } = useParams()
  const navigate = useNavigate()
  const [facilitator, setFacilitator] = useState(null)
  const [pairs, setPairs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  // Edit mode state
  const [editMode, setEditMode] = useState(false)
  const [editData, setEditData] = useState(null)
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState(null)
  const [editSuccess, setEditSuccess] = useState(null) // string message or null

  // Email change state (separate from profile edit)
  const [newEmail, setNewEmail] = useState('')
  const [emailLoading, setEmailLoading] = useState(false)
  const [emailError, setEmailError] = useState(null)

  const loadData = useCallback(async () => {
    try {
      const { facilitator, pairs } = await db.getFacilitatorDetail(id)
      setFacilitator(facilitator)
      setPairs(pairs)
    } catch (err) {
      if (isAbortError(err)) return
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleAction = async (action) => {
    const message = action === 'delete'
      ? t('facilitatorManagement.confirmDelete', { email: facilitator.email })
      : t('facilitatorManagement.confirmAction', { action })
    if (!confirm(message)) return
    setActionLoading(true)
    try {
      await callEdgeFunction('approve-facilitator', { facilitator_id: id, action })
      if (action === 'delete') {
        navigate('/admin/facilitators')
        return
      }
      await loadData()
    } catch (err) {
      setError(err.message)
    } finally {
      setActionLoading(false)
    }
  }

  const enterEditMode = () => {
    setEditData({
      salutation: facilitator.salutation || '',
      full_name: facilitator.full_name || '',
      organization_name: facilitator.organization_name || '',
      organization_address: facilitator.organization_address || '',
      professional_role: facilitator.professional_role || '',
      professional_role_other: facilitator.professional_role_other || '',
    })
    setNewEmail('')
    setEditError(null)
    setEditSuccess(null)
    setEmailError(null)
    setEditMode(true)
  }

  const cancelEdit = () => {
    setEditMode(false)
    setEditData(null)
    setEditError(null)
    setEmailError(null)
  }

  const updateEditField = (field) => (e) => {
    setEditData(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    setEditError(null)
    setEditSuccess(null)

    if (!editData.full_name.trim()) {
      setEditError(t('facilitatorDetail.editMode.fullNameRequired'))
      return
    }

    if (!editData.organization_name.trim()) {
      setEditError(t('facilitatorDetail.editMode.organisationRequired'))
      return
    }

    if (!editData.professional_role) {
      setEditError(t('facilitatorDetail.editMode.roleRequired'))
      return
    }

    if (editData.professional_role === 'Others' && !editData.professional_role_other.trim()) {
      setEditError(t('facilitatorDetail.editMode.specifyRoleRequired'))
      return
    }

    setEditLoading(true)
    try {
      const updates = { ...editData }
      if (updates.professional_role !== 'Others') {
        updates.professional_role_other = ''
      }
      await db.updateFacilitatorProfile(id, updates)
      await loadData()
      setEditSuccess(t('facilitatorDetail.editMode.profileUpdated'))
      setTimeout(() => setEditSuccess(null), 3000)
    } catch (err) {
      setEditError(err.message)
    } finally {
      setEditLoading(false)
    }
  }

  const handleEmailChange = async (e) => {
    e.preventDefault()
    setEmailError(null)

    const trimmed = newEmail.trim().toLowerCase()
    if (!trimmed) {
      setEmailError(t('facilitatorDetail.emailChange.enterEmail'))
      return
    }

    if (trimmed === facilitator.email.toLowerCase()) {
      setEmailError(t('facilitatorDetail.emailChange.sameEmail'))
      return
    }

    setEmailLoading(true)
    try {
      await callEdgeFunction('update-facilitator-email', {
        facilitator_id: id,
        email: trimmed,
      })
      await loadData()
      setNewEmail('')
      setEditSuccess(t('facilitatorDetail.emailChange.emailUpdated'))
      setTimeout(() => setEditSuccess(null), 3000)
    } catch (err) {
      setEmailError(err.message)
    } finally {
      setEmailLoading(false)
    }
  }

  if (loading) {
    return <div className="text-center py-12 text-gray-500">{t('facilitatorDetail.loadingDetail')}</div>
  }

  if (!facilitator) {
    return <div className="text-center py-12 text-gray-500">{t('facilitatorDetail.notFound')}</div>
  }

  return (
    <div className="animate-fadeIn">
      <div className="mb-6">
        <Link to="/admin/facilitators" className="text-gray-500 hover:text-gray-700 font-semibold transition-colors">
          {t('facilitatorDetail.backToFacilitators')}
        </Link>
      </div>

      <div className="bg-white rounded-xl p-8 shadow-sm mb-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="font-display text-2xl font-medium text-gray-800">{facilitator.salutation ? `${translateSalutation(facilitator.salutation)} ${facilitator.full_name}` : facilitator.full_name}</h2>
            <p className="text-gray-500">{facilitator.email}</p>
            {facilitator.pending_email && (
              <p className="text-xs text-amber-600 mt-1">
                {t('facilitatorDetail.pendingEmailChange', { email: facilitator.pending_email })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${STATUS_STYLES[facilitator.status]}`}>
              {t(`facilitatorDetail.statusLabels.${facilitator.status}`)}
            </span>
            {facilitator.status === 'pending_approval' && (
              <>
                <button
                  onClick={() => handleAction('approve')}
                  disabled={actionLoading}
                  className="px-4 py-2 text-sm font-semibold text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                >
                  {t('facilitatorDetail.actions.approve')}
                </button>
                <button
                  onClick={() => handleAction('reject')}
                  disabled={actionLoading}
                  className="px-4 py-2 text-sm font-semibold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                >
                  {t('facilitatorDetail.actions.reject')}
                </button>
              </>
            )}
            {facilitator.status === 'active' && (
              <button
                onClick={() => handleAction('suspend')}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-semibold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                {t('facilitatorDetail.actions.suspend')}
              </button>
            )}
            {(facilitator.status === 'suspended' || facilitator.status === 'rejected') && (
              <button
                onClick={() => handleAction('reactivate')}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-semibold text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
              >
                {t('facilitatorDetail.actions.reactivate')}
              </button>
            )}
            {(facilitator.status === 'rejected' || facilitator.status === 'suspended') && (
              <button
                onClick={() => handleAction('delete')}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-semibold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                {t('facilitatorDetail.actions.delete')}
              </button>
            )}
            {!editMode && (
              <button
                onClick={enterEditMode}
                className="px-4 py-2 text-sm font-semibold text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
              >
                {t('facilitatorDetail.editProfile')}
              </button>
            )}
          </div>
        </div>

        <ErrorAlert message={error} onDismiss={() => setError(null)} />

        {editSuccess && (
          <div className="mb-4 text-sm font-semibold text-green-600 bg-green-50 rounded-lg px-4 py-3">
            {editSuccess}
          </div>
        )}

        {editMode ? (
          /* Edit mode */
          <div>
            <ErrorAlert message={editError} onDismiss={() => setEditError(null)} />

            <form onSubmit={handleEditSubmit} className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-1">{t('facilitatorDetail.editMode.salutationLabel')}</label>
                  <select
                    className={inputClass}
                    value={editData.salutation}
                    onChange={updateEditField('salutation')}
                  >
                    <option value="">{t('facilitatorDetail.editMode.salutationPlaceholder')}</option>
                    {SALUTATIONS.map((s, idx) => (
                      <option key={s} value={s}>{Array.isArray(salutationLabels) ? salutationLabels[idx] || s : s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-1">{t('facilitatorDetail.editMode.fullNameLabel')}</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={editData.full_name}
                    onChange={updateEditField('full_name')}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">{t('facilitatorDetail.editMode.organisationNameLabel')}</label>
                <input
                  type="text"
                  className={inputClass}
                  value={editData.organization_name}
                  onChange={updateEditField('organization_name')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">{t('facilitatorDetail.editMode.organisationAddressLabel')}</label>
                <textarea
                  className={inputClass + ' resize-none'}
                  rows={2}
                  value={editData.organization_address}
                  onChange={updateEditField('organization_address')}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">{t('facilitatorDetail.editMode.professionalRoleLabel')}</label>
                <select
                  className={inputClass}
                  value={editData.professional_role}
                  onChange={updateEditField('professional_role')}
                  required
                >
                  <option value="">{t('facilitatorDetail.editMode.professionalRolePlaceholder')}</option>
                  {PROFESSIONAL_ROLES.map((role, idx) => (
                    <option key={role} value={role}>{Array.isArray(roleLabels) ? roleLabels[idx] || role : role}</option>
                  ))}
                </select>
              </div>

              {editData.professional_role === 'Others' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-1">{t('facilitatorDetail.editMode.specifyRoleLabel')}</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={editData.professional_role_other}
                    onChange={updateEditField('professional_role_other')}
                    required
                  />
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={editLoading}
                  className="px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg hover:shadow-md transition-all disabled:opacity-70"
                >
                  {editLoading ? t('facilitatorDetail.editMode.saving') : t('facilitatorDetail.editMode.saveProfile')}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-5 py-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('facilitatorDetail.editMode.cancel')}
                </button>
              </div>
            </form>

            {/* Email change section */}
            <div className="border-t border-gray-200 pt-5">
              <h3 className="text-sm font-semibold text-gray-600 mb-3">{t('facilitatorDetail.emailChange.title')}</h3>
              <p className="text-sm text-gray-500 mb-1">{t('facilitatorDetail.emailChange.currentEmail', { email: facilitator.email })}</p>

              <ErrorAlert message={emailError} onDismiss={() => setEmailError(null)} />

              <form onSubmit={handleEmailChange} className="flex items-end gap-3 mt-3">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-600 mb-1">{t('facilitatorDetail.emailChange.newEmailLabel')}</label>
                  <input
                    type="email"
                    className={inputClass}
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder={t('facilitatorDetail.emailChange.newEmailPlaceholder')}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={emailLoading}
                  className="px-5 py-2 text-sm font-semibold text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors disabled:opacity-70 whitespace-nowrap"
                >
                  {emailLoading ? t('facilitatorDetail.emailChange.updating') : t('facilitatorDetail.emailChange.updateEmail')}
                </button>
              </form>
              <p className="text-xs text-gray-400 mt-2">
                {t('facilitatorDetail.emailChange.adminNote')}
              </p>
            </div>
          </div>
        ) : (
          /* View mode */
          <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <dt className="text-sm font-semibold text-gray-500">{t('facilitatorDetail.viewMode.organisation')}</dt>
              <dd className="text-gray-800">{facilitator.organization_name}</dd>
            </div>
            <div>
              <dt className="text-sm font-semibold text-gray-500">{t('facilitatorDetail.viewMode.address')}</dt>
              <dd className="text-gray-800">{facilitator.organization_address}</dd>
            </div>
            <div>
              <dt className="text-sm font-semibold text-gray-500">{t('facilitatorDetail.viewMode.professionalRole')}</dt>
              <dd className="text-gray-800">
                {facilitator.professional_role === 'Others' ? facilitator.professional_role_other : translateRole(facilitator.professional_role)}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-semibold text-gray-500">{t('facilitatorDetail.viewMode.registered')}</dt>
              <dd className="text-gray-800">{formatDate(facilitator.created_at, locale)}</dd>
            </div>
            {facilitator.approved_at && (
              <div>
                <dt className="text-sm font-semibold text-gray-500">{t('facilitatorDetail.viewMode.approved')}</dt>
                <dd className="text-gray-800">{formatDate(facilitator.approved_at, locale)}</dd>
              </div>
            )}
          </dl>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="font-display text-lg font-medium text-gray-800">
            {t('facilitatorDetail.clientPairs.title', { count: pairs.length })}
          </h3>
        </div>

        {pairs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">{t('facilitatorDetail.clientPairs.noPairs')}</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorDetail.clientPairs.tableHeaders.maleClient')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorDetail.clientPairs.tableHeaders.femaleClient')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorDetail.clientPairs.tableHeaders.status')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorDetail.clientPairs.tableHeaders.created')}</th>
                <th className="text-right px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorDetail.clientPairs.tableHeaders.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pairs.map(pair => {
                const status = getPairStatus(pair, t)
                const bothComplete = pair.male_respondent_id && pair.female_respondent_id
                return (
                  <tr key={pair.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-800">{pair.male_name}</div>
                      <div className="text-gray-500 text-sm">{pair.male_email}</div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-800">{pair.female_name}</div>
                      <div className="text-gray-500 text-sm">{pair.female_email}</div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${status.style}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-500 text-sm">
                      {formatDate(pair.created_at, locale)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      {bothComplete && pair.male_respondent_id && (
                        <Link
                          to={`/admin/couple-analysis/${pair.male_respondent_id}`}
                          className="px-3 py-1.5 text-xs font-semibold text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                        >
                          {t('facilitatorDetail.clientPairs.coupleAnalysis')}
                        </Link>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
